import { useEffect, useMemo, useRef, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CircularProgress from '@mui/material/CircularProgress';
import { useActiveNavigationIndicator } from 'components/ui/hooks';
import {
  getNavigationActionContentPosition,
  type NavigationActionVisualState,
  useNavigationActionContentWidth,
  useNavigationActionFeedback,
} from 'components/ui/navigationActionFeedback';
import { OverflowTooltip } from 'components/ui/OverflowTooltip';
import { Skeleton } from 'components/ui/Skeleton';
import { Tooltip } from 'components/ui/Tooltip';
import { getCssPx } from 'components/ui/utilities/zoom';
import { DEFAULT_ZOOM_LEVEL } from 'context/ZoomContext';
import ScalarIcon from 'icons/Scalar';
import { PrismThemeProvider } from 'theme';
import type {
  SecondaryNavigationActionButtonProps,
  SecondaryNavigationAllocationStatusBadgeProps,
  SecondaryNavigationIconButtonProps,
  SecondaryNavigationInfoLabelProps,
  SecondaryNavigationProps,
  SecondaryNavigationReadyForAuditBadgeProps,
  SecondaryNavigationScalarOwnerBadgeProps,
  SecondaryNavigationSelectorProps,
  SecondaryNavigationSkeletonProps,
  SecondaryNavigationSlotProps,
  SecondaryNavigationStatusBadgeProps,
} from './SecondaryNavigation.props';
import {
  SecondaryNavigationActionButtonContent,
  SecondaryNavigationActionButtonContentItem,
  SecondaryNavigationActionButtonRoot,
  SecondaryNavigationActionSavedCheck,
  SecondaryNavigationActionSavedCheckPath,
  SecondaryNavigationActionStatusIcon,
  SecondaryNavigationActiveIndicator,
  SecondaryNavigationContent,
  SecondaryNavigationIconButtonRoot,
  SecondaryNavigationIconTooltipAnchor,
  SecondaryNavigationInfoLabelRoot,
  SecondaryNavigationInfoLabelText,
  SecondaryNavigationInfoLabelValue,
  SecondaryNavigationReadyForAuditBadgeRoot,
  SecondaryNavigationRoot,
  SecondaryNavigationScalarOwnerBadgeRoot,
  SecondaryNavigationSelectorLabel,
  SecondaryNavigationSelectorRoot,
  SecondaryNavigationSelectorValue,
  SecondaryNavigationSkeletonTabs,
  SecondaryNavigationStatusBadgeRoot,
  SecondaryNavigationStatusBadgeText,
  SecondaryNavigationTab,
  SecondaryNavigationTabLabel,
  SecondaryNavigationTabs,
  SecondaryNavigationTabsFrame,
  SecondaryNavigationTitle,
  SecondaryNavigationTitleGroup,
  SecondaryNavigationActions as StyledSecondaryNavigationActions,
  SecondaryNavigationStatus as StyledSecondaryNavigationStatus,
  SecondaryNavigationTitleAccessory as StyledSecondaryNavigationTitleAccessory,
} from './SecondaryNavigation.styles';
import { SECONDARY_NAVIGATION_TEST_IDS } from './SecondaryNavigation.testIds';
import {
  createSecondaryNavigationSkeletonItems,
  getSecondaryNavigationAllocationStatusBadgeConfig,
  getSecondaryNavigationTextTooltipTitle,
  SECONDARY_NAVIGATION_STATUS_BADGE_ENTER_MS,
  SECONDARY_NAVIGATION_STATUS_BADGE_EXIT_MS,
} from './SecondaryNavigation.utils';

const SECONDARY_NAVIGATION_TABS_SCROLLER_SELECTOR = '.MuiTabs-scroller';

const getSecondaryNavigationActionStatusIcon = (visualState: NavigationActionVisualState) => {
  if (visualState === 'saving') {
    return (
      <SecondaryNavigationActionStatusIcon aria-hidden="true">
        <CircularProgress color="inherit" size="inherit" thickness={5} />
      </SecondaryNavigationActionStatusIcon>
    );
  }

  if (visualState === 'saved') {
    return (
      <SecondaryNavigationActionStatusIcon aria-hidden="true">
        <SecondaryNavigationActionSavedCheck viewBox="0 0 16 16">
          <SecondaryNavigationActionSavedCheckPath d="M3 8.25 6.5 11.5 13 5" pathLength={1} />
        </SecondaryNavigationActionSavedCheck>
      </SecondaryNavigationActionStatusIcon>
    );
  }

  if (visualState === 'error') {
    return (
      <SecondaryNavigationActionStatusIcon aria-hidden="true">
        <ErrorOutlineIcon fontSize="inherit" />
      </SecondaryNavigationActionStatusIcon>
    );
  }

  return null;
};

const SecondaryNavigation = ({
  actions,
  ariaLabel = 'Secondary navigation',
  items = [],
  onChange,
  status,
  title,
  titleAccessory,
  titleLeading,
  titleTooltip = typeof title === 'string' ? title : undefined,
  value = false,
  zoomLevel = DEFAULT_ZOOM_LEVEL,
  ...rootProps
}: SecondaryNavigationProps) => {
  const tabsFrameRef = useRef<HTMLDivElement | null>(null);
  const tabValues = useMemo(() => items.map(item => item.value), [items]);
  const {
    setActiveIndicatorRef,
    setActiveItemRef,
  } = useActiveNavigationIndicator({
    activeValue: value,
    containerRef: tabsFrameRef,
    itemValues: tabValues,
    scrollElementSelector: SECONDARY_NAVIGATION_TABS_SCROLLER_SELECTOR,
  });

  return (
    <PrismThemeProvider>
      <SecondaryNavigationRoot
        {...rootProps}
        aria-label={ariaLabel}
        data-testid={SECONDARY_NAVIGATION_TEST_IDS.root}
        zoomLevel={zoomLevel}>
        <SecondaryNavigationContent zoomLevel={zoomLevel}>
          {(title != null || titleLeading != null) && (
            <SecondaryNavigationTitleGroup zoomLevel={zoomLevel}>
              {titleLeading}
              {title != null && (
                <OverflowTooltip title={titleTooltip ?? ''}>
                  <SecondaryNavigationTitle data-testid={SECONDARY_NAVIGATION_TEST_IDS.title} zoomLevel={zoomLevel}>
                    {title}
                  </SecondaryNavigationTitle>
                </OverflowTooltip>
              )}
            </SecondaryNavigationTitleGroup>
          )}
          {titleAccessory && (
            <StyledSecondaryNavigationTitleAccessory
              data-testid={SECONDARY_NAVIGATION_TEST_IDS.titleAccessory}
              zoomLevel={zoomLevel}>
              {titleAccessory}
            </StyledSecondaryNavigationTitleAccessory>
          )}
          {status && (
            <StyledSecondaryNavigationStatus data-testid={SECONDARY_NAVIGATION_TEST_IDS.status} zoomLevel={zoomLevel}>
              {status}
            </StyledSecondaryNavigationStatus>
          )}
          {items.length > 0 && (
            <SecondaryNavigationTabsFrame ref={tabsFrameRef}>
              <SecondaryNavigationActiveIndicator ref={setActiveIndicatorRef} aria-hidden="true" />
              <SecondaryNavigationTabs value={value} onChange={onChange}>
                {items.map(item => (
                  <SecondaryNavigationTab
                    key={item.value}
                    ref={setActiveItemRef(item.value)}
                    value={item.value}
                    label={(
                      <OverflowTooltip title={getSecondaryNavigationTextTooltipTitle(item.label)}>
                        <SecondaryNavigationTabLabel>{item.label}</SecondaryNavigationTabLabel>
                      </OverflowTooltip>
                    )}
                    disabled={item.disabled}
                    disableRipple
                    data-testid={SECONDARY_NAVIGATION_TEST_IDS.tab(item.value)}
                    zoomLevel={zoomLevel}
                  />
                ))}
              </SecondaryNavigationTabs>
            </SecondaryNavigationTabsFrame>
          )}
        </SecondaryNavigationContent>
        {actions && (
          <StyledSecondaryNavigationActions data-testid={SECONDARY_NAVIGATION_TEST_IDS.actions} zoomLevel={zoomLevel}>
            {actions}
          </StyledSecondaryNavigationActions>
        )}
      </SecondaryNavigationRoot>
    </PrismThemeProvider>
  );
};

export const SecondaryNavigationSkeleton = ({
  actionCount = 2,
  ariaLabel = 'Loading secondary navigation',
  itemCount = 4,
  showActions = true,
  showStatus = true,
  showTitleAccessory = false,
  zoomLevel = DEFAULT_ZOOM_LEVEL,
}: SecondaryNavigationSkeletonProps) => (
  <PrismThemeProvider>
    <SecondaryNavigationRoot
      aria-label={ariaLabel}
      data-testid={SECONDARY_NAVIGATION_TEST_IDS.skeleton}
      zoomLevel={zoomLevel}>
      <SecondaryNavigationContent zoomLevel={zoomLevel}>
        <Skeleton
          height={getCssPx(16)}
          variant="text"
          width={getCssPx(128)}
        />
        {showTitleAccessory && (
          <Skeleton
            height={getCssPx(24)}
            radius={getCssPx(4)}
            variant="pill"
            width={getCssPx(104)}
          />
        )}
        {showStatus && (
          <Skeleton
            height={getCssPx(18)}
            radius={getCssPx(4)}
            width={getCssPx(52)}
          />
        )}
        <SecondaryNavigationSkeletonTabs>
          {createSecondaryNavigationSkeletonItems(itemCount).map(index => (
            <Skeleton
              key={index}
              height={getCssPx(12)}
              variant="text"
              width={getCssPx(index === 0 ? 58 : 72)}
            />
          ))}
        </SecondaryNavigationSkeletonTabs>
      </SecondaryNavigationContent>
      {showActions && (
        <StyledSecondaryNavigationActions zoomLevel={zoomLevel}>
          {createSecondaryNavigationSkeletonItems(actionCount).map(index => (
            <Skeleton
              key={index}
              height={getCssPx(24)}
              radius={getCssPx(4)}
              variant="pill"
              width={getCssPx(index === 0 ? 120 : 24)}
            />
          ))}
        </StyledSecondaryNavigationActions>
      )}
    </SecondaryNavigationRoot>
  </PrismThemeProvider>
);

export const SecondaryNavigationTitleAccessory = ({ children }: SecondaryNavigationSlotProps) => (
  <StyledSecondaryNavigationTitleAccessory zoomLevel={DEFAULT_ZOOM_LEVEL}>
    {children}
  </StyledSecondaryNavigationTitleAccessory>
);

export const SecondaryNavigationStatus = ({ children }: SecondaryNavigationSlotProps) => (
  <StyledSecondaryNavigationStatus zoomLevel={DEFAULT_ZOOM_LEVEL}>{children}</StyledSecondaryNavigationStatus>
);

export const SecondaryNavigationStatusBadge = ({
  children,
  tone = 'draft',
  ...props
}: SecondaryNavigationStatusBadgeProps) => (
  <SecondaryNavigationStatusBadgeRoot tone={tone} {...props}>{children}</SecondaryNavigationStatusBadgeRoot>
);

export const SecondaryNavigationAllocationStatusBadge = ({
  status,
  ...props
}: SecondaryNavigationAllocationStatusBadgeProps) => {
  const nextStatusConfig = useMemo(
    () => (status === null || status === undefined ? null : getSecondaryNavigationAllocationStatusBadgeConfig(status)),
    [status]
  );
  const [displayStatusConfig, setDisplayStatusConfig] = useState(nextStatusConfig);
  const displayStatusConfigRef = useRef(displayStatusConfig);
  const [textState, setTextState] = useState<'entering' | 'exiting' | 'idle'>('idle');

  // Fade the previous status label out before swapping text so short/long status changes do not overlap.
  // The badge tone updates immediately so color does not flash halfway through the text transition.
  useEffect(() => {
    const currentStatusConfig = displayStatusConfigRef.current;

    if (!nextStatusConfig) {
      displayStatusConfigRef.current = null;
      setDisplayStatusConfig(null);
      setTextState('idle');
      return undefined;
    }

    if (!currentStatusConfig) {
      displayStatusConfigRef.current = nextStatusConfig;
      setDisplayStatusConfig(nextStatusConfig);
      setTextState('idle');
      return undefined;
    }

    if (currentStatusConfig.label === nextStatusConfig.label) {
      return undefined;
    }

    setTextState('exiting');

    const exitTimeoutId = window.setTimeout(() => {
      displayStatusConfigRef.current = nextStatusConfig;
      setDisplayStatusConfig(nextStatusConfig);
      setTextState('entering');
    }, SECONDARY_NAVIGATION_STATUS_BADGE_EXIT_MS);
    const enterTimeoutId = window.setTimeout(() => {
      setTextState('idle');
    }, SECONDARY_NAVIGATION_STATUS_BADGE_EXIT_MS + SECONDARY_NAVIGATION_STATUS_BADGE_ENTER_MS);

    return () => {
      window.clearTimeout(exitTimeoutId);
      window.clearTimeout(enterTimeoutId);
    };
  }, [nextStatusConfig]);

  if (!displayStatusConfig) {
    return null;
  }

  const { label } = displayStatusConfig;
  const tone = nextStatusConfig?.tone ?? displayStatusConfig.tone;

  return (
    <SecondaryNavigationStatusBadgeRoot
      data-secondary-navigation-allocation-status-badge="true"
      data-testid={`secondary-navigation-allocation-status-badge-${label.toLowerCase()}`}
      tone={tone}
      {...props}>
      <SecondaryNavigationStatusBadgeText key={label} textState={textState}>
        {label}
      </SecondaryNavigationStatusBadgeText>
    </SecondaryNavigationStatusBadgeRoot>
  );
};

export const SecondaryNavigationReadyForAuditBadge = ({
  children = 'Ready for Audit',
  visible = true,
  ...props
}: SecondaryNavigationReadyForAuditBadgeProps) => (
  <SecondaryNavigationReadyForAuditBadgeRoot
    aria-hidden={visible ? undefined : true}
    data-secondary-navigation-ready-for-audit-badge="true"
    data-testid="ready-for-audit-badge"
    isVisible={visible}
    {...props}>
    {children}
  </SecondaryNavigationReadyForAuditBadgeRoot>
);

export const SecondaryNavigationScalarOwnerBadge = ({
  tooltipTitle = 'Scalar Valuation',
  visible = true,
  ...props
}: SecondaryNavigationScalarOwnerBadgeProps) => (
  <Tooltip placement="bottom" title={visible ? tooltipTitle : ''}>
    <SecondaryNavigationScalarOwnerBadgeRoot
      aria-hidden={visible ? undefined : true}
      aria-label={visible ? 'Scalar-owned valuation' : undefined}
      data-testid="secondary-navigation-scalar-owner-badge"
      isVisible={visible}
      {...props}>
      <ScalarIcon />
    </SecondaryNavigationScalarOwnerBadgeRoot>
  </Tooltip>
);

export const SecondaryNavigationActions = ({ children }: SecondaryNavigationSlotProps) => (
  <StyledSecondaryNavigationActions zoomLevel={DEFAULT_ZOOM_LEVEL}>{children}</StyledSecondaryNavigationActions>
);

export const SecondaryNavigationInfoLabel = ({
  label,
  value,
  valueTone = 'default',
  'data-testid': dataTestId,
  valueTestId,
  ...props
}: SecondaryNavigationInfoLabelProps) => (
  <SecondaryNavigationInfoLabelRoot data-testid={dataTestId} {...props}>
    <OverflowTooltip title={getSecondaryNavigationTextTooltipTitle(label)}>
      <SecondaryNavigationInfoLabelText>{label}</SecondaryNavigationInfoLabelText>
    </OverflowTooltip>
    <OverflowTooltip title={getSecondaryNavigationTextTooltipTitle(value)}>
      <SecondaryNavigationInfoLabelValue
        data-testid={valueTestId ?? (dataTestId ? `${dataTestId}-value` : undefined)}
        valueTone={valueTone}>
        {value}
      </SecondaryNavigationInfoLabelValue>
    </OverflowTooltip>
  </SecondaryNavigationInfoLabelRoot>
);

export const SecondaryNavigationActionButton = ({
  children,
  'data-testid': dataTestId,
  disabled,
  endIcon,
  errorLabel = 'Error',
  isSaving = false,
  onClick,
  saveFailed = false,
  savedLabel = 'Saved',
  savingLabel = 'Saving...',
  startIcon,
  variant = 'secondary',
  ...props
}: SecondaryNavigationActionButtonProps) => {
  const { handleClick, isActionDisabled, visualState } = useNavigationActionFeedback({
    disabled,
    isSaving,
    onClick,
    saveFailed,
  });
  const {
    contentWidth,
    errorContentRef,
    idleContentRef,
    savedContentRef,
    savingContentRef,
  } = useNavigationActionContentWidth(visualState, {
    errorLabel,
    idleContent: children,
    savedLabel,
    savingLabel,
  });
  const statusIcon = getSecondaryNavigationActionStatusIcon(visualState);

  return (
    <SecondaryNavigationActionButtonRoot
      {...props}
      aria-busy={visualState === 'saving' || undefined}
      aria-disabled={isActionDisabled || undefined}
      buttonState={visualState}
      buttonVariant={variant}
      data-testid={dataTestId}
      disabled={disabled}
      disableRipple
      endIcon={statusIcon ?? endIcon}
      onClick={handleClick}
      startIcon={visualState === 'idle' ? startIcon : undefined}>
      <SecondaryNavigationActionButtonContent style={contentWidth ? { width: contentWidth } : undefined}>
        <SecondaryNavigationActionButtonContentItem
          ref={idleContentRef}
          contentPosition={getNavigationActionContentPosition(visualState, 'idle')}>
          {children}
        </SecondaryNavigationActionButtonContentItem>
        <SecondaryNavigationActionButtonContentItem
          ref={savingContentRef}
          contentPosition={getNavigationActionContentPosition(visualState, 'saving')}>
          {savingLabel}
        </SecondaryNavigationActionButtonContentItem>
        <SecondaryNavigationActionButtonContentItem
          ref={savedContentRef}
          contentPosition={getNavigationActionContentPosition(visualState, 'saved')}>
          {savedLabel}
        </SecondaryNavigationActionButtonContentItem>
        <SecondaryNavigationActionButtonContentItem
          ref={errorContentRef}
          contentPosition={getNavigationActionContentPosition(visualState, 'error')}>
          {errorLabel}
        </SecondaryNavigationActionButtonContentItem>
      </SecondaryNavigationActionButtonContent>
    </SecondaryNavigationActionButtonRoot>
  );
};

export const SecondaryNavigationIconButton = ({
  active,
  ariaLabel,
  icon,
  tooltipTitle = ariaLabel,
  variant = 'plain',
  ...props
}: SecondaryNavigationIconButtonProps) => {
  const button = (
    <SecondaryNavigationIconButtonRoot
      aria-label={ariaLabel}
      iconButtonVariant={variant}
      isActive={active}
      disableRipple
      {...props}>
      {icon}
    </SecondaryNavigationIconButtonRoot>
  );

  return (
    <Tooltip placement="bottom" title={tooltipTitle}>
      <SecondaryNavigationIconTooltipAnchor>
        {button}
      </SecondaryNavigationIconTooltipAnchor>
    </Tooltip>
  );
};

const SECONDARY_NAVIGATION_SELECTOR_POSITIVE_FEEDBACK_MS = 1200;

export const SecondaryNavigationSelector = ({
  endIcon = <ArrowDropDownIcon fontSize="inherit" />,
  label,
  positiveFeedbackDurationMs = SECONDARY_NAVIGATION_SELECTOR_POSITIVE_FEEDBACK_MS,
  positiveFeedbackKey,
  tooltipTitle,
  value,
  valueTestId,
  valueTone = 'default',
  variant = 'default',
  ...props
}: SecondaryNavigationSelectorProps) => {
  const [isPositiveFeedbackVisible, setIsPositiveFeedbackVisible] = useState(false);
  const isPositiveFeedbackReadyRef = useRef(false);
  const previousPositiveFeedbackKeyRef = useRef(positiveFeedbackKey);
  const positiveFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Flash only after an owner-confirmed selector value change, never on initial hydration.
  useEffect(() => {
    if (positiveFeedbackKey === undefined) {
      return undefined;
    }

    if (!isPositiveFeedbackReadyRef.current) {
      isPositiveFeedbackReadyRef.current = true;
      previousPositiveFeedbackKeyRef.current = positiveFeedbackKey;
      return undefined;
    }

    if (Object.is(previousPositiveFeedbackKeyRef.current, positiveFeedbackKey)) {
      return undefined;
    }

    previousPositiveFeedbackKeyRef.current = positiveFeedbackKey;
    setIsPositiveFeedbackVisible(true);

    if (positiveFeedbackTimerRef.current) {
      clearTimeout(positiveFeedbackTimerRef.current);
    }

    positiveFeedbackTimerRef.current = setTimeout(() => {
      setIsPositiveFeedbackVisible(false);
    }, positiveFeedbackDurationMs);

    return undefined;
  }, [positiveFeedbackDurationMs, positiveFeedbackKey]);

  // Clear pending feedback timers if the selector unmounts during navigation.
  useEffect(() => () => {
    if (positiveFeedbackTimerRef.current) {
      clearTimeout(positiveFeedbackTimerRef.current);
    }
  }, []);

  const resolvedValueTone = isPositiveFeedbackVisible ? 'positive' : valueTone;

  const selector = (
    <SecondaryNavigationSelectorRoot endIcon={endIcon} selectorVariant={variant} disableRipple {...props}>
      {label ? (
        <OverflowTooltip title={getSecondaryNavigationTextTooltipTitle(label)}>
          <SecondaryNavigationSelectorLabel>{label}</SecondaryNavigationSelectorLabel>
        </OverflowTooltip>
      ) : null}
      <OverflowTooltip title={getSecondaryNavigationTextTooltipTitle(value)}>
        <SecondaryNavigationSelectorValue data-testid={valueTestId} valueTone={resolvedValueTone}>
          {value}
        </SecondaryNavigationSelectorValue>
      </OverflowTooltip>
    </SecondaryNavigationSelectorRoot>
  );

  if (!tooltipTitle) {
    return selector;
  }

  return (
    <Tooltip placement="bottom" title={tooltipTitle}>
      <SecondaryNavigationIconTooltipAnchor>
        {selector}
      </SecondaryNavigationIconTooltipAnchor>
    </Tooltip>
  );
};

export default SecondaryNavigation;
